import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle,
  AlertCircle,
  Shield,
  FileText,
  Lock,
  Phone,
} from "lucide-react";
import {
  registerUser,
  registerPurohit,
  registerTemple,
} from "../../services/api";
import toast from "react-hot-toast";

const AgreementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formData = location.state?.formData;

  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowContent(true), 100);
  }, []);

  if (!formData) {
    navigate("/register");
    return null;
  }

  const handleCompleteRegistration = async () => {
    if (!agreed) {
      setError("Please accept the terms and conditions to continue");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      let response;

      if (formData.accountType === "purohit") {
        const purohitPayload = {
          fullName: formData.fullName?.trim(),
          phone: formData.phone?.trim(),
          email: formData.email?.toLowerCase().trim(),
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          address: formData.address?.trim(),
          city: formData.city?.trim(),
          pincode: formData.pincode?.trim(),
          acceptTerms: true,
        };

        console.log("📤 Purohit Registration Payload:", purohitPayload);
        response = await registerPurohit(purohitPayload);
        console.log("✅ Purohit Registration Response:", response);
      } else if (formData.accountType === "user") {
        const userPayload = {
          fullName: formData.fullName.trim(),
          phone: formData.phone.trim(),
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          acceptTerms: true,
        };

        console.log("📤 User Registration Payload:", userPayload);
        response = await registerUser(userPayload);
        console.log("✅ User Registration Response:", response);
      } else if (formData.accountType === "temple") {
        const templePayload = {
          fullName: formData.fullName.trim(),
          phone: formData.phone.trim(),
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          acceptTerms: true,
        };
        console.log("📤 Temple Registration Payload:", templePayload);
        response = await registerTemple(templePayload);
        console.log("✅ User Registration Response:", response);
      }

      const responseData = response.data || response;
      const userData =
        responseData.purohit || responseData.user || responseData.temple || {};
      const userId = userData.id || responseData.id;
      const token = responseData.token || "temp-token-" + Date.now();

      toast.success("Registration successful! Account created.");

      localStorage.setItem("authToken", token);
      
      if (userId) localStorage.setItem("userId", String(userId));
      localStorage.setItem("userEmail", formData.email);
      localStorage.setItem("userName", formData.fullName);
      localStorage.setItem("accountType", formData.accountType);
      localStorage.setItem("isLoggedIn", "true");

      if (formData.accountType === "purohit") {
        localStorage.setItem("purohitAddress", formData.address || "");
        localStorage.setItem("purohitCity", formData.city || "");
        localStorage.setItem("purohitpincode", formData.pincode || "");
        if (userId) localStorage.setItem("purohitId", String(userId));
        localStorage.setItem("isComplete", "false");
      }

      if (formData.accountType === "temple" && userId) {
        localStorage.setItem("templeId", String(userId));
      }

      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Account created successfully! Please log in.",
            email: formData.email,
          },
        });
      }, 1500);
    } catch (error) {
      console.error("❌ Registration error:", error);

      let errorMessage = "Registration failed. Please try again.";

      if (error.response?.data) {
        const errorData = error.response.data;

        if (
          typeof errorData === "string" &&
          errorData.includes("<!DOCTYPE html>")
        ) {
          errorMessage =
            "Server error occurred. Please check if all fields are filled correctly.";
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors) {
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors
              .map((err) => {
                if (typeof err === "string") return err;
                if (err.message) return err.message;
                if (err.msg) return err.msg;
                return JSON.stringify(err);
              })
              .join(", ");
          } else if (typeof errorData.errors === "object") {
            errorMessage = Object.entries(errorData.errors)
              .map(([key, value]) => `${key}: ${value}`)
              .join(", ");
          }
        }
      }

      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-start justify-center px-4 sm:px-6 md:px-8 py-6 md:py-8 mt-12">
      <div
        className={`w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-700 transform ${
          showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* HEADER */}
        <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 px-4 sm:px-6 md:px-8 py-8 md:py-10 pb-16">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 sm:w-9 sm:h-9 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                Terms & Privacy Agreement
              </h1>
              <p className="text-orange-50 text-sm sm:text-base">
                Please review and accept our terms and privacy policy to
                complete registration
              </p>
            </div>
          </div>
        </div>

        {/* CONTENT AREA */}
        <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 mt-4">
          {/* Error */}
          {error && (
            <div className="mb-5 p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm animate-shake">
              <p className="text-red-700 text-sm sm:text-base flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </p>
            </div>
          )}

          {/* TERMS & CONDITIONS */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden mb-6 shadow-inner">
            <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 sm:px-5 py-3 border-b-2 border-gray-200 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">
                Terms and Conditions
              </h2>
            </div>

            <div className="p-4 sm:p-6 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto custom-scrollbar">
              <div className="space-y-5 text-sm sm:text-base text-gray-700">
                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      1
                    </span>
                    Acceptance of Terms
                  </h3>
                  <p className="leading-relaxed">
                    By creating an account with Purohit App, you agree to be
                    bound by these Terms and Conditions. If you do not agree to
                    these terms, please do not use our services. These terms
                    constitute a legally binding agreement between you and
                    Purohit App.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      2
                    </span>
                    User Accounts
                  </h3>
                  <p className="leading-relaxed mb-3">
                    You are responsible for maintaining the confidentiality of
                    your account credentials. You agree to:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Accept responsibility for all activities under your
                        account
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Notify us immediately of any unauthorized access
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Provide accurate and truthful information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Keep your account information up to date</span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      3
                    </span>
                    Service Provider Obligations
                  </h3>
                  <p className="leading-relaxed mb-3">
                    If you are registering as a Purohit (service provider), you
                    agree to:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Provide accurate information about your services and
                        qualifications
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Maintain professional conduct when providing services
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Honor all confirmed bookings unless exceptional
                        circumstances prevent it
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Comply with all applicable laws and regulations
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Deliver services as described in your profile</span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      4
                    </span>
                    User Obligations
                  </h3>
                  <p className="leading-relaxed mb-3">
                    If you are registering as a User (customer), you agree to:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Provide accurate booking information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Treat service providers with respect and dignity
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Make timely payments for services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Cancel bookings according to our cancellation policy
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Be present at the scheduled time for services</span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      5
                    </span>
                    Payment and Refunds
                  </h3>
                  <p className="leading-relaxed mb-3">
                    All payments must be made through the platform. Our payment
                    terms include:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Service fees are clearly displayed before booking
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Refunds are subject to our cancellation policy
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Platform fees are non-refundable</span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      6
                    </span>
                    Limitation of Liability
                  </h3>
                  <p className="leading-relaxed">
                    Purohit App acts as a platform connecting users and service
                    providers. We are not liable for the quality of services,
                    disputes between parties, or any damages arising from the
                    use of our platform. Users and service providers engage at
                    their own risk.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      7
                    </span>
                    Prohibited Conduct
                  </h3>
                  <p className="leading-relaxed mb-3">
                    Users are prohibited from:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Using the platform for illegal activities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Harassing or abusing other users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Posting false or misleading information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Circumventing platform payments</span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      8
                    </span>
                    Modifications to Terms
                  </h3>
                  <p className="leading-relaxed">
                    We reserve the right to modify these terms at any time.
                    Continued use of the platform after changes constitutes
                    acceptance of the modified terms.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      9
                    </span>
                    Account Termination
                  </h3>
                  <p className="leading-relaxed">
                    We reserve the right to suspend or terminate accounts that
                    violate these terms or engage in fraudulent activity.
                  </p>
                </section>
              </div>
            </div>
          </div>

          {/* PRIVACY POLICY */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden mb-6 shadow-inner">
            <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 sm:px-5 py-3 border-b-2 border-gray-200 flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">
                Privacy Policy
              </h2>
            </div>

            <div className="p-4 sm:p-6 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto custom-scrollbar">
              <div className="space-y-5 text-sm sm:text-base text-gray-700">
                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      1
                    </span>
                    Information We Collect
                  </h3>
                  <p className="leading-relaxed mb-3">
                    We collect the following types of information:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Personal Information:</strong> Name, email
                        address, phone number, address
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Service Information:</strong> Booking details,
                        service preferences, reviews
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Payment Information:</strong> Transaction
                        history (payment details are handled securely by our
                        payment processor)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Usage Data:</strong> Device information, IP
                        address, browser type, usage patterns
                      </span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      2
                    </span>
                    How We Use Your Information
                  </h3>
                  <p className="leading-relaxed mb-3">
                    Your information is used to:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Provide and improve our services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Process bookings and payments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Communicate with you about services and updates
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Ensure platform security and prevent fraud</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Analyze usage patterns to improve user experience
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Send promotional communications (with your consent)
                      </span>
                    </li>
                  </ul>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      3
                    </span>
                    Information Sharing
                  </h3>
                  <p className="leading-relaxed mb-3">
                    We share your information only in the following
                    circumstances:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>With Service Providers:</strong> Booking
                        information is shared with purohits to fulfill services
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>With Customers:</strong> Purohit profile
                        information is shared with booking customers
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Payment Processors:</strong> Secure payment
                        information for transactions
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Legal Requirements:</strong> When required by
                        law or to protect our rights
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Business Transfers:</strong> In the event of
                        merger or acquisition
                      </span>
                    </li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    We never sell your personal information to third parties.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      4
                    </span>
                    Data Security
                  </h3>
                  <p className="leading-relaxed mb-3">
                    We implement industry-standard security measures to protect
                    your data:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        Encryption of sensitive data in transit and at rest
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Regular security audits and updates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Secure password hashing and authentication</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Limited employee access to personal data</span>
                    </li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    However, no method of transmission over the internet is 100%
                    secure. We cannot guarantee absolute security.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      5
                    </span>
                    Your Rights
                  </h3>
                  <p className="leading-relaxed mb-3">
                    You have the following rights regarding your personal data:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Access:</strong> Request a copy of your personal
                        data
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Correction:</strong> Update or correct
                        inaccurate information
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Deletion:</strong> Request deletion of your
                        account and data
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Opt-out:</strong> Unsubscribe from marketing
                        communications
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>
                        <strong>Portability:</strong> Receive your data in a
                        portable format
                      </span>
                    </li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    To exercise these rights, contact us at
                    privacy@purohitapp.com
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      6
                    </span>
                    Cookies and Tracking
                  </h3>
                  <p className="leading-relaxed mb-3">
                    We use cookies and similar technologies to:
                  </p>
                  <ul className="space-y-2 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Maintain your login session</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Remember your preferences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Analyze site usage and improve performance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Deliver personalized content</span>
                    </li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    You can control cookies through your browser settings.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      7
                    </span>
                    Data Retention
                  </h3>
                  <p className="leading-relaxed">
                    We retain your personal data for as long as necessary to
                    provide our services and comply with legal obligations. When
                    you delete your account, we will remove or anonymize your
                    data within 90 days, except where required by law to retain
                    it longer.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      8
                    </span>
                    Children's Privacy
                  </h3>
                  <p className="leading-relaxed">
                    Our services are not intended for users under 18 years of
                    age. We do not knowingly collect information from children.
                    If we become aware that a child has provided us with
                    personal data, we will delete it immediately.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      9
                    </span>
                    Changes to Privacy Policy
                  </h3>
                  <p className="leading-relaxed">
                    We may update this Privacy Policy periodically. We will
                    notify you of significant changes via email or through the
                    platform. Your continued use after changes indicates
                    acceptance of the updated policy.
                  </p>
                </section>

                <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                      10
                    </span>
                    Contact Information
                  </h3>
                  <p className="leading-relaxed mb-2">
                    For questions about this Privacy Policy or your data,
                    contact us at:
                  </p>
                  <ul className="space-y-1 ml-2">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Email: privacy@purohitapp.com</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2"></span>
                      <span>Support: support@purohitapp.com</span>
                    </li>
                  </ul>
                </section>
              </div>
            </div>
          </div>

          {/* CHECKBOX */}
          <div className="mb-6 bg-orange-50/50 rounded-xl p-4 border-2 border-orange-100">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  if (error) setError("");
                }}
                className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 cursor-pointer mt-0.5"
              />

              <label className="text-sm sm:text-base text-gray-700 leading-relaxed cursor-pointer">
                I have read and agree to the Terms & Conditions and Privacy
                Policy. I understand my rights and obligations as a user.
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={() => navigate("/register")}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3.5 sm:py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Go Back
            </button>

            <button
              onClick={handleCompleteRegistration}
              disabled={!agreed || isSubmitting}
              className={`flex-1 px-6 py-3.5 sm:py-4 rounded-xl font-semibold transition-all relative overflow-hidden ${
                !agreed || isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white hover:from-orange-600 hover:via-orange-700 hover:to-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Complete Registration
                </span>
              )}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs sm:text-sm text-center text-gray-500">
              By completing registration, you acknowledge that you have read and
              understood our Terms & Conditions and Privacy Policy
            </p>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #fb923c;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #f97316;
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default AgreementPage;
